/**
 * The AI model catalogue's dialogs (TMMPDM-0104, TMMAPI-0171 section A): Add model…, Edit tiers…
 * and Retire…. Every refusal shows in the dialog that made the call, which stays open (TMM-B4), and
 * each dialog keeps one request in flight. The provider check is the server's: an add Anthropic
 * doesn't confirm is never saved, and the console only words what the server answered.
 * TMMPDM-0105 (TMMAPI-0172): Add model words 409 AI_MODEL_NOT_SUPPORTED by `details.reason`.
 */
import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
  useTheme,
  Typography,
} from '@mui/material';
import { ApiError, errorCopy, isValidationFailed, validationFields } from '@/api/errors';
import { adminApi } from '@/api/tmm/admin';
import type { AiModelCatalogPatchRequest, AiModelCatalogRow } from '@/api/tmm/types';
import { ConfirmDialog, SubmitButton } from '@/components/page';
import { useCopy } from '@/hooks/useCopy';
import {
  inUseCopy,
  notSupportedCopy,
  unavailableRefusal,
  type CatalogRefusal,
} from './modelCatalogCopy';

const NOTE_MAX = 500;
const MODEL_ID_MAX = 64;
const MODEL_ID_RULE = /^[a-z0-9][a-z0-9.-]*$/;

type Translate = ReturnType<typeof useCopy>['t'];

export function RefusalAlert({ refusal }: { refusal: CatalogRefusal }) {
  return (
    <Alert severity="error">
      {refusal.text}
      {refusal.reason ? (
        <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
          {refusal.reason}
        </Typography>
      ) : null}
    </Alert>
  );
}

function TierChecks({
  fast,
  deep,
  onChange,
  error,
}: {
  fast: boolean;
  deep: boolean;
  onChange: (next: { fast: boolean; deep: boolean }) => void;
  error: string | undefined;
}) {
  const { t } = useCopy();
  return (
    <FormControl component="fieldset" required error={Boolean(error)}>
      <FormLabel component="legend">{t('ai.catalog.form.tiers', 'Tiers')}</FormLabel>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={fast}
              onChange={(event) => onChange({ fast: event.target.checked, deep })}
            />
          }
          label={t('ai.catalog.tier.FAST', 'Fast')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={deep}
              onChange={(event) => onChange({ fast, deep: event.target.checked })}
            />
          }
          label={t('ai.catalog.tier.DEEP', 'Deep')}
        />
      </FormGroup>
      {error ? <FormHelperText>{error}</FormHelperText> : null}
    </FormControl>
  );
}

function tooLong(t: Translate): string {
  return t('datedSettings.form.tooLong', 'Use at most {{max, number}} characters.', {
    max: NOTE_MAX,
  });
}

function noTier(t: Translate): string {
  return t('ai.catalog.form.tierRequired', 'Choose at least one tier.');
}

// ---------------------------------------------------------------------------------------------

interface AddModelDialogProps {
  open: boolean;
  onClose: () => void;
  onAdded: (row: AiModelCatalogRow) => void;
}

export function AddModelDialog({ open, onClose, onAdded }: AddModelDialogProps) {
  const { t } = useCopy();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="add-model-title">
      <DialogTitle id="add-model-title">{t('ai.catalog.add.title', 'Add model')}</DialogTitle>
      {open ? <AddModelFields onClose={onClose} onAdded={onAdded} /> : null}
    </Dialog>
  );
}

function AddModelFields({ onClose, onAdded }: Omit<AddModelDialogProps, 'open'>) {
  const { t } = useCopy();
  const theme = useTheme();
  const [modelId, setModelId] = useState('');
  const [tiers, setTiers] = useState({ fast: false, deep: false });
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [refusal, setRefusal] = useState<CatalogRefusal | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const ruleCopy = t(
    'ai.catalog.form.modelIdRule',
    'Lower-case letters, numbers, dots and hyphens. Exactly as Anthropic names it.',
  );

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    const id = modelId.trim();
    if (id === '') next.modelId = t('ai.form.required', 'Required.');
    else if (id.length > MODEL_ID_MAX || !MODEL_ID_RULE.test(id)) next.modelId = ruleCopy;
    if (!tiers.fast && !tiers.deep) next.fast = noTier(t);
    if (note.trim().length > NOTE_MAX) next.note = tooLong(t);
    return next;
  };

  const submit = async () => {
    if (inFlight.current) return;
    const next = validate();
    setErrors(next);
    setRefusal(null);
    if (Object.keys(next).length > 0) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const trimmedNote = note.trim();
      const row = await adminApi.addAiModel({
        modelId: modelId.trim(),
        fast: tiers.fast,
        deep: tiers.deep,
        ...(trimmedNote !== '' ? { note: trimmedNote } : {}),
      });
      onAdded(row);
    } catch (err) {
      const code = err instanceof ApiError ? String(err.code) : '';
      if (code === 'AI_MODEL_NOT_AT_PROVIDER') {
        setErrors({
          modelId: t(
            'ai.catalog.notAtProvider',
            "Anthropic doesn't know that model id. Check the spelling.",
          ),
        });
      } else if (code === 'AI_MODEL_EXISTS') {
        setRefusal({ text: t('ai.catalog.exists', 'That model is already in the catalogue.') });
      } else if (code === 'AI_MODEL_NOT_SUPPORTED' && err instanceof ApiError) {
        setRefusal({
          text:
            notSupportedCopy(t, err) ??
            errorCopy(err, t, t('ai.catalog.add.error', "Couldn't add the model.")),
        });
      } else if (code === 'AI_PROVIDER_UNAVAILABLE' && err instanceof ApiError) {
        setRefusal(unavailableRefusal(t, err));
      } else if (err instanceof ApiError && isValidationFailed(err)) {
        const marked = validationFields(err, t, {
          modelId: ruleCopy,
          fast: noTier(t),
          deep: noTier(t),
          note: tooLong(t),
        });
        const mapped: Record<string, string> = {};
        if (marked.modelId) mapped.modelId = marked.modelId;
        if (marked.fast ?? marked.deep) mapped.fast = noTier(t);
        if (marked.note) mapped.note = marked.note;
        setErrors(mapped);
        if (Object.keys(mapped).length === 0) {
          setRefusal({ text: t('ai.catalog.add.error', "Couldn't add the model.") });
        }
      } else {
        setRefusal({
          text: errorCopy(err, t, t('ai.catalog.add.error', "Couldn't add the model.")),
        });
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {refusal ? <RefusalAlert refusal={refusal} /> : null}
          <TextField
            id="add-model-id"
            label={t('ai.catalog.form.modelId', 'Model id')}
            value={modelId}
            onChange={(event) => {
              setModelId(event.target.value);
              setErrors(({ modelId: _dropped, ...others }) => others);
            }}
            required
            error={Boolean(errors.modelId)}
            helperText={errors.modelId ?? ruleCopy}
            fullWidth
            slotProps={{
              htmlInput: {
                autoCapitalize: 'none',
                spellCheck: false,
                style: theme.typography.mono,
              },
            }}
          />
          <TierChecks
            fast={tiers.fast}
            deep={tiers.deep}
            onChange={(nextTiers) => {
              setTiers(nextTiers);
              setErrors(({ fast: _dropped, ...others }) => others);
            }}
            error={errors.fast}
          />
          <TextField
            id="add-model-note"
            label={t('datedSettings.form.note', 'Note (optional)')}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
              setErrors(({ note: _dropped, ...others }) => others);
            }}
            error={Boolean(errors.note)}
            helperText={errors.note}
            multiline
            minRows={2}
            fullWidth
            slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
          />
          <Typography variant="body2">
            {t('ai.catalog.add.consequence', 'We check the id with Anthropic before saving it.')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton busy={busy} onClick={() => void submit()}>
          {t('ai.catalog.add.submit', 'Add model')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}

// ---------------------------------------------------------------------------------------------

interface EditTiersDialogProps {
  row: AiModelCatalogRow | null;
  onClose: () => void;
  onSaved: () => void;
  /** Re-read after a 404. */
  onReread: () => void;
}

export function EditTiersDialog({ row, onClose, onSaved, onReread }: EditTiersDialogProps) {
  const { t } = useCopy();
  return (
    <Dialog
      open={row !== null}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="edit-tiers-title"
    >
      <DialogTitle id="edit-tiers-title">
        {t('ai.catalog.edit.title', 'Edit tiers: {{model}}', { model: row?.modelId ?? '' })}
      </DialogTitle>
      {row ? (
        <EditTiersFields
          key={row.id}
          row={row}
          onClose={onClose}
          onSaved={onSaved}
          onReread={onReread}
        />
      ) : null}
    </Dialog>
  );
}

function EditTiersFields({
  row,
  onClose,
  onSaved,
  onReread,
}: Omit<EditTiersDialogProps, 'row'> & { row: AiModelCatalogRow }) {
  const { t } = useCopy();
  const [tiers, setTiers] = useState({ fast: row.fast, deep: row.deep });
  const [note, setNote] = useState(row.note ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [refusal, setRefusal] = useState<CatalogRefusal | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const submit = async () => {
    if (inFlight.current) return;
    const next: Record<string, string> = {};
    if (!tiers.fast && !tiers.deep) next.fast = noTier(t);
    if (note.trim().length > NOTE_MAX) next.note = tooLong(t);
    setErrors(next);
    setRefusal(null);
    if (Object.keys(next).length > 0) return;
    // Only the changed keys. An emptied note is sent as null, which clears it (the YAML's rule).
    const body: AiModelCatalogPatchRequest = {};
    if (tiers.fast !== row.fast) body.fast = tiers.fast;
    if (tiers.deep !== row.deep) body.deep = tiers.deep;
    const trimmed = note.trim();
    if (trimmed !== (row.note ?? '')) body.note = trimmed === '' ? null : trimmed;
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    inFlight.current = true;
    setBusy(true);
    try {
      await adminApi.patchAiModel(row.id, body);
      onSaved();
    } catch (err) {
      const code = err instanceof ApiError ? String(err.code) : '';
      if (code === 'AI_MODEL_IN_USE' && err instanceof ApiError) {
        setRefusal({ text: inUseCopy(t, err) });
      } else if (err instanceof ApiError && isValidationFailed(err)) {
        const marked = validationFields(err, t, {
          fast: noTier(t),
          deep: noTier(t),
          note: tooLong(t),
        });
        const mapped: Record<string, string> = {};
        if (marked.fast ?? marked.deep) mapped.fast = noTier(t);
        if (marked.note) mapped.note = marked.note;
        setErrors(mapped);
        if (Object.keys(mapped).length === 0) {
          setRefusal({ text: t('ai.catalog.edit.error', "Couldn't change the tiers.") });
        }
      } else if (code === 'NOT_FOUND' || (err instanceof ApiError && err.status === 404)) {
        setRefusal({
          text: errorCopy(err, t, t('ai.catalog.notFound', "That model couldn't be found.")),
        });
        onReread();
      } else {
        setRefusal({
          text: errorCopy(err, t, t('ai.catalog.edit.error', "Couldn't change the tiers.")),
        });
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {refusal ? <RefusalAlert refusal={refusal} /> : null}
          <TierChecks
            fast={tiers.fast}
            deep={tiers.deep}
            onChange={(nextTiers) => {
              setTiers(nextTiers);
              setErrors(({ fast: _dropped, ...others }) => others);
            }}
            error={errors.fast}
          />
          <TextField
            id="edit-tiers-note"
            label={t('datedSettings.form.note', 'Note (optional)')}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
              setErrors(({ note: _dropped, ...others }) => others);
            }}
            error={Boolean(errors.note)}
            helperText={errors.note}
            multiline
            minRows={2}
            fullWidth
            slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton busy={busy} onClick={() => void submit()}>
          {t('ai.catalog.edit.submit', 'Save')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}

// ---------------------------------------------------------------------------------------------

interface RetireDialogProps {
  row: AiModelCatalogRow | null;
  onClose: () => void;
  onRetired: (row: AiModelCatalogRow) => void;
  onReread: () => void;
}

export function RetireModelDialog({ row, onClose, onRetired, onReread }: RetireDialogProps) {
  const { t } = useCopy();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [openedFor, setOpenedFor] = useState<number | null>(null);
  if (!row && openedFor !== null) setOpenedFor(null);
  if (row && row.id !== openedFor) {
    setOpenedFor(row.id);
    setNote('');
    setError(null);
  }

  const confirm = async () => {
    if (!row || inFlight.current) return;
    if (note.trim().length > NOTE_MAX) {
      setError(tooLong(t));
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const trimmed = note.trim();
      await adminApi.retireAiModel(row.id, trimmed === '' ? undefined : trimmed);
      onRetired(row);
    } catch (err) {
      const code = err instanceof ApiError ? String(err.code) : '';
      if (code === 'AI_MODEL_IN_USE' && err instanceof ApiError) {
        setError(inUseCopy(t, err));
      } else if (code === 'NOT_FOUND' || (err instanceof ApiError && err.status === 404)) {
        setError(errorCopy(err, t, t('ai.catalog.notFound', "That model couldn't be found.")));
        onReread();
      } else {
        setError(errorCopy(err, t, t('ai.catalog.retire.error', "Couldn't retire the model.")));
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <ConfirmDialog
      open={row !== null}
      title={t('ai.catalog.retire.title', 'Retire a model')}
      consequence={
        <Stack spacing={2}>
          <span>
            {t('ai.catalog.retire.question', 'Retire {{model}}? Its prices and history stay.', {
              model: row?.modelId ?? '',
            })}
          </span>
          <TextField
            label={t('datedSettings.form.note', 'Note (optional)')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
          />
        </Stack>
      }
      confirmLabel={t('ai.catalog.retire.confirm', 'Retire')}
      destructive
      busy={busy}
      error={error}
      onConfirm={() => void confirm()}
      onCancel={onClose}
    />
  );
}
